'use client';

import Image from 'next/image';
import { m, Variants } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';
import styles from './Services.module.css';

const services = [
    {
        icon: '🚛',
        image: '/images/service-darat.png',
        title: 'Cargo Darat',
        description: 'Armada lengkap (CDE, CDD, Fuso, Wingbox) untuk area Jawa-Bali-Sumatera. Charter & Cunsul Truck tersedia.',
        features: ['Door-to-door delivery', 'Charter & Cunsul Truck', 'Jawa-Bali-Sumatera coverage', 'Tracking real-time'],
        link: '/layanan/kargo-darat',
    },
    {
        icon: '🚢',
        image: '/images/service-laut.png',
        title: 'Cargo Laut',
        description: 'Fokus pada Fast Vessel untuk rute Indonesia Timur. Layanan FCL & LCL dengan berbagai jenis kapal.',
        features: ['Fast Vessel (Trucking - RoRo)', 'PELNI Vessel (Kapal Putih)', 'Cargo Container (LCL & FCL)', 'Port-to-Port & Door-to-Door'],
        link: '/layanan/kargo-laut',
    },
    {
        icon: '✈️',
        image: '/images/service-udara.png',
        title: 'Cargo Udara',
        description: 'Solusi Door-to-Door super cepat for barang mendesak ke seluruh Indonesia.',
        features: ['Door-to-Door delivery', 'Pengiriman super cepat', 'Barang mendesak & bernilai tinggi', 'Seluruh tujuan Indonesia'],
        link: '/layanan/kargo-udara',
    },
    {
        icon: '🏭',
        image: '/images/service-warehouse.png',
        title: 'Warehousing',
        description: 'Fasilitas gudang modern dengan sistem manajemen inventori terintegrasi.',
        features: ['Gudang berstandar nasional', 'Inventory management', 'Pick & pack service', 'Cross-docking'],
        link: '/layanan/pergudangan',
    },
    {
        icon: '🛻',
        image: '/images/service-towing.png',
        title: 'Towing & Gendong Mobil',
        description: 'Layanan derek dan kirim mobil 24 jam untuk wilayah Solo Raya dan sekitarnya.',
        features: ['Derek 24 Jam', 'Gendong Mobil Antar Kota', 'Layanan Solo Raya', 'Emergency Roadside Assistance'],
        link: '/layanan/towing',
    },
];

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.2 }
    }
};

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function Services() {
    return (
        <section id="layanan" className={`section ${styles.services}`}>
            <div className="container">
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="section-header"
                >
                    <span className="section-label">Layanan Unggulan</span>
                    <h2 className="section-title">Solusi Logistik Terpadu</h2>
                    <p className="section-subtitle">
                        Kami menyediakan berbagai layanan pengiriman cargo yang disesuaikan
                        dengan kebutuhan bisnis Anda, didukung teknologi dan armada terbaik.
                    </p>
                </m.div>

                <m.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-50px" }}
                    className={styles.grid}
                >
                    {services.map((service, index) => (
                        <Link href={service.link} key={service.title} className={styles.card}>
                            <m.div
                                variants={cardVariants}
                                whileHover={{ y: -10 }}
                                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                            <div className={styles.cardImageWrapper}>
                                <Image
                                    src={service.image}
                                    alt={service.title}
                                    fill
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                                    className={styles.cardImage}
                                />
                                <div className={styles.imageOverlay} />
                                <div className={styles.iconWrap}>
                                    <span className={styles.icon}>{service.icon}</span>
                                </div>
                            </div>

                            <div className={styles.cardContent}>
                                <h3 className={styles.cardTitle}>{service.title}</h3>
                                <p className={styles.cardDescription}>{service.description}</p>

                                <ul className={styles.featureList}>
                                    {service.features.map((feature, idx) => (
                                        <li key={idx} className={styles.featureItem}>
                                            <CheckCircle2 size={16} className={styles.checkIcon} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className={styles.cardLink}>
                                    Pelajari Lebih Lanjut
                                    <ArrowRight size={16} className={styles.linkIcon} />
                                </div>
                            </div>
                            </m.div>
                        </Link>
                    ))}
                </m.div>
            </div>
        </section>
    );
}
